import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { formatDateFull } from "../../utils/format";

function esc(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export default function WorkshopMap({ workshops, selectedId, onSelect, onEdit }) {
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const containerRef = useRef(null);
  const onEditRef = useRef(onEdit);
  onEditRef.current = onEdit;

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, { zoomControl: true }).setView([49.8, 15.5], 7);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap přispěvatelé",
      maxZoom: 19,
    }).addTo(map);
    map.on("popupopen", (e) => {
      const node = e.popup.getElement();
      if (!node) return;
      const btn = node.querySelector(".lucka-edit-btn");
      if (btn) {
        btn.onclick = () => onEditRef.current && onEditRef.current(btn.getAttribute("data-id"));
      }
    });
    mapRef.current = map;
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    Object.values(markersRef.current).forEach((m) => map.removeLayer(m));
    markersRef.current = {};

    const pts = [];
    workshops.forEach((w) => {
      if (w.lat == null || w.lng == null) return;
      const color = w.status_color || "#2563eb";
      const isSel = w.id === selectedId;
      const size = isSel ? 20 : 15;
      const icon = L.divIcon({
        className: "",
        html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:${isSel ? 3 : 2}px solid ${isSel ? "#0a0a0a" : "#fff"};box-shadow:0 1px 4px rgba(0,0,0,.35)"></div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });
      const marker = L.marker([w.lat, w.lng], { icon }).addTo(map);
      marker.bindPopup(
        `<div style="font-family:Inter,sans-serif;font-size:13px;min-width:180px">
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:2px">
            <span style="width:9px;height:9px;border-radius:50%;background:${color};display:inline-block"></span>
            <strong>${esc(w.school_name)}</strong>
          </div>
          <div style="color:#71717a">${formatDateFull(w.date)} · ${esc(w.time_from)}</div>
          <div style="color:#71717a;margin-bottom:6px">${esc(w.address)}</div>
          <div style="color:#71717a;margin-bottom:8px">${esc(w.status_name)}${w.program_name ? " · " + esc(w.program_name) : ""}</div>
          <button class="lucka-edit-btn" data-id="${w.id}" style="cursor:pointer;width:100%;padding:6px 10px;border-radius:6px;background:#0a0a0a;color:#fff;border:none;font-size:12px;font-weight:500">Otevřít detail / upravit</button>
        </div>`
      );
      marker.on("click", () => onSelect && onSelect(w.id));
      markersRef.current[w.id] = marker;
      pts.push([w.lat, w.lng]);
    });

    if (pts.length && !selectedId) {
      map.fitBounds(pts, { padding: [40, 40], maxZoom: 11 });
    }
  }, [workshops, selectedId, onSelect]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedId) return;
    const w = workshops.find((x) => x.id === selectedId);
    if (w && w.lat != null) {
      map.setView([w.lat, w.lng], 13, { animate: true });
      const m = markersRef.current[selectedId];
      if (m) m.openPopup();
    }
  }, [selectedId, workshops]);

  return <div ref={containerRef} className="h-full w-full" data-testid="workshop-map" />;
}
